/**
 * Writes a complex extension (an `object` or `array` section) back onto a
 * resource while keeping every sub-extension the schema does not own. One
 * implementation for the Kora config and for any resource schema with a
 * section.
 */
import type { Extension } from "@medplum/fhirtypes";

import type { ConfigSchema } from "@/types/clinic-configuration";
import { isNumber } from "@/lib/utils/typeof";

import {
  buildSectionExtensions,
  fromSectionDraft,
  getSectionOwnedUrls,
  isWritableField,
} from "./configuration-schema-runtime";

/**
 * Rebuilds one block's sub-extensions from a complete, validated draft for
 * it. A page whose section holds a field the shared layer cannot write — a
 * `weekSchedule`, whose sub-slugs only that page knows — passes its own.
 */
type BuildBlock = (draft: Record<string, unknown>) => Extension[];

/**
 * The block builder for a section the shared layer can write on its own.
 * Throws instead of skipping a `weekSchedule`: its stored sub-extensions are
 * owned by the section, so skipping it would delete them on every save.
 */
export const buildBlockExtensions = (
  section: ConfigSchema.Section,
  draft: Record<string, unknown>
): Extension[] => {
  if (!Object.values(section.fields).every(isWritableField)) {
    throw new Error(
      `Section "${section.slug}" has a field the shared layer cannot write. Pass a block builder.`
    );
  }

  return buildSectionExtensions(
    section.fields,
    fromSectionDraft(
      section.fields,
      draft as ConfigSchema.SectionDraft<ConfigSchema.Shape>
    )
  );
};

/**
 * Merge, don't replace: the schema-owned sub-extensions are rebuilt from the
 * draft, and anything else already stored under that block survives — e.g. a
 * field the schema hasn't caught up to yet.
 */
export const mergeBlockExtension = (
  stored: Extension | undefined,
  section: ConfigSchema.Section,
  draft: Record<string, unknown>,
  buildBlock: BuildBlock = (blockDraft) =>
    buildBlockExtensions(section, blockDraft)
): Extension => {
  const ownedUrls = getSectionOwnedUrls(section.fields);
  const preserved = (stored?.extension ?? []).filter(
    ({ url }) => !ownedUrls.has(url)
  );

  return {
    url: section.slug,
    extension: [...preserved, ...buildBlock(draft)],
  };
};

/**
 * An `array` section's update carries the whole list, so its blocks are
 * rebuilt rather than rewritten one by one: a removed entry has no block to
 * rewrite, and an added one has no block to rewrite *into*.
 *
 * Foreign sub-extensions come from the stored block at the entry's
 * `sourceIndex` — the position it was parsed from, and the only thing that
 * still points at the right block after an add or a remove. A new entry has
 * none and preserves nothing.
 */
export const mergeArraySectionExtensions = (
  storedBlocks: Extension[],
  section: ConfigSchema.Section,
  entries: ConfigSchema.ArrayEntryDraft[],
  buildBlock?: BuildBlock
): Extension[] =>
  entries.map((entry) =>
    mergeBlockExtension(
      isNumber(entry.sourceIndex) ? storedBlocks[entry.sourceIndex] : undefined,
      section,
      entry,
      buildBlock
    )
  );

/**
 * One section's merged blocks: the whole list for an `array` section, one
 * block for an `object` section (`0..1` by definition).
 *
 * `draft` is untyped because the draft provider is generic across pages:
 * the caller knows which section it belongs to, this function does not.
 */
export const mergeSectionExtensions = (
  storedBlocks: Extension[],
  section: ConfigSchema.Section,
  draft: unknown,
  buildBlock?: BuildBlock
): Extension[] => {
  const blocks =
    section.kind === "array"
      ? mergeArraySectionExtensions(
          storedBlocks,
          section,
          draft as ConfigSchema.ArrayEntryDraft[],
          buildBlock
        )
      : [
          mergeBlockExtension(
            storedBlocks[0],
            section,
            draft as Record<string, unknown>,
            buildBlock
          ),
        ];

  /**
   * An extension carrying neither a value nor children is invalid FHIR
   * (`ext-1`), and a block whose every field was cleared builds exactly that.
   * Dropping it writes "nothing set" the way the resource already spells it:
   * the block is absent, which the parser reads back as empty.
   */
  return blocks.filter((block) => block.extension?.length);
};
